import { loadSessionHistory } from "./events.js";
import { HttpClient, query, type HttpOptions, type RequestOptions } from "./http.js";
import { runAndWait } from "./runs.js";
import { uploadFile } from "./uploads.js";
import type {
  HostingView,
  HostingVerifyResult,
  ApiKeyView,
  AuditEntry,
  AuthConfig,
  BeginUploadResult,
  BundleContent,
  BundlePatch,
  BundleContentView,
  BundleVersionResult,
  BundleVersionView,
  BundleView,
  CreateRunInput,
  CreateRunResult,
  CredentialView,
  ProjectView,
  EnvironmentDoc,
  IssuedApiKey,
  LogEntry,
  Me,
  Metrics,
  Page,
  RcaBody,
  ResolvedEnvironment,
  Role,
  RunView,
  RunnerType,
  Scope,
  SessionView,
  UploadView,
  UsageAggregate,
  PlatformCloudAccess,
} from "./types.js";

/**
 * 全エンドポイントの型付きラッパ（FR-3）。
 *
 * 入口は 1 つ。機能ごとの名前空間で整理する（Q1）。
 * 網羅性は `API_ROUTES` との突き合わせで機械的に検査する（AC-1 / AC-2）。
 */

export type RcaClientOptions = HttpOptions;

const json = (body: unknown): RequestOptions => ({ body: JSON.stringify(body) });

/**
 * 居場所の指定。
 *
 * ★ 絞り込みではない。付ければプロジェクトの中から見た結果になり、
 * テナントのものも一緒に見える。付けなければテナント直下から見た結果。
 */
export interface PlaceQuery {
  readonly projectId?: string | null;
}

const placeQuery = (place: PlaceQuery): string =>
  query({ projectId: place.projectId ?? undefined });

export class RcaClient {
  readonly http: HttpClient;

  constructor(options: RcaClientOptions = {}) {
    this.http = new HttpClient(options);
  }

  /**
   * 実行して完了まで待つ（FR-4.1）。
   *
   * 進捗は SSE で受け取り、**最終状態は必ず取り直す**ので、
   * 購読が途中で切れても結果を取り違えない。
   */
  run(input: Parameters<typeof runAndWait>[1]): ReturnType<typeof runAndWait> {
    return runAndWait(this, input);
  }

  /**
   * ファイルを置く（FR-4.3）。
   *
   * 単発か 3 段かは**サイズを見て SDK が選ぶ**。
   * 上限で弾かれたら正しい方式でやり直す。
   */
  upload(
    body: Parameters<typeof uploadFile>[1],
    input?: Parameters<typeof uploadFile>[2],
    options?: Parameters<typeof uploadFile>[3],
  ): ReturnType<typeof uploadFile> {
    return uploadFile(this, body, input, options);
  }

  /** セッションの過去のやり取りを読み直す（FR-4.4）。 */
  history(
    sessionId: string,
    options?: Parameters<typeof loadSessionHistory>[2],
  ): ReturnType<typeof loadSessionHistory> {
    return loadSessionHistory(this, sessionId, options);
  }

  /**
   * OpenAPI 仕様。認証は不要。
   *
   * `API_ROUTES`（＝ v1 の業務エンドポイント一覧）には含まれない、
   * メタ情報の口。API ドキュメント画面がこれを読んで描く。
   */
  openapi(): Promise<Record<string, unknown>> {
    return this.http.request<Record<string, unknown>>("/openapi.json");
  }

  /** 死活確認。 */
  healthz(): Promise<{ ok: boolean }> {
    return this.http.request<{ ok: boolean }>("/healthz");
  }

  /**
   * テナントの置き場所（デプロイ UI）。
   *
   * ★ **共通基盤にしかない口。** 各クラウドのデプロイは 1 テナント専用なので、
   * 置き場所を選ぶ・確かめるという処理を持たない。
   */
  /**
   * 基盤の設定（顧客のクラウドに入るための、我々の側の資格）。
   *
   * ★ **基盤の管理者だけ。** 全顧客の同意がここで指したアプリを通るので、
   * 触れるのは構成で決めた 1 テナントの管理者に限られる（それ以外は 403）。
   * 構成が無ければ口ごと生えない（404）。
   */
  readonly platformCloudAccess = {
    /** いまの状態。★ 資格は返らない。 */
    list: (): Promise<{ items: PlatformCloudAccess[] }> =>
      this.http.request<{ items: PlatformCloudAccess[] }>("/v1/platform/cloud-access"),
    /** 入れる・入れ替える。★ 資格は書き込みのみ。 */
    put: (cloud: string, setting: Readonly<Record<string, string>>): Promise<void> =>
      this.http.request<void>(`/v1/platform/cloud-access/${encodeURIComponent(cloud)}`, {
        method: "PUT",
        ...json(setting),
      }),
    /** 外す。★ 外すとそのクラウドは選択肢から消える。 */
    remove: (cloud: string): Promise<void> =>
      this.http.request<void>(`/v1/platform/cloud-access/${encodeURIComponent(cloud)}`, {
        method: "DELETE",
      }),
  };

  readonly hosting = {
    /** いまの状態と、選んだクラウドの手順。 */
    get: (): Promise<HostingView> => this.http.request<HostingView>("/v1/hosting"),
    /**
     * 初めて選ぶ。★ あとから変えられない。
     *
     * @param target 顧客の識別子（`HostingView.targets[cloud]` の欄）。
     *   ★ **自分のクラウドでは要る。** 無いと同意の URL のテナントが
     *   空になり、手順が埋まらないまま顧客に届く。既定のクラウドでは
     *   顧客の作業が無いので渡さない。
     */
    claim: (cloud: string, target?: Readonly<Record<string, string>>): Promise<HostingView> =>
      this.http.request<HostingView>("/v1/hosting", {
        method: "POST",
        ...json(target === undefined ? { cloud } : { cloud, target }),
      }),
    /**
     * 入れたか確かめる。
     *
     * ★ **押すのは顧客。** 一式を立てるのは顧客の作業なので、我々は
     * 「立ったか」を確かめるだけ（待たない）。
     */
    verify: (): Promise<HostingVerifyResult> =>
      this.http.request<HostingVerifyResult>("/v1/hosting/verify", { method: "POST" }),
  };

  // ---------------------------------------------------------------- auth
  readonly auth = {
    /** ログイン方法。認証不要。 */
    config: (): Promise<AuthConfig> => this.http.request<AuthConfig>("/v1/auth/config"),
    me: (): Promise<Me> => this.http.request<Me>("/v1/auth/me"),
  };

  // ---------------------------------------------------------------- runs
  readonly runs = {
    create: (input: CreateRunInput): Promise<CreateRunResult> =>
      this.http.request<CreateRunResult>("/v1/runs", {
        method: "POST",
        ...json(input),
        // 冪等キーがあるときだけ再試行が効く（Q6）。
        ...(input.idempotencyKey === undefined ? {} : { idempotencyKey: input.idempotencyKey }),
      }),

    list: (
      params: {
        status?: string;
        sessionId?: string;
        projectId?: string;
        limit?: number;
        cursor?: string;
      } = {},
    ): Promise<Page<RunView>> => this.http.request<Page<RunView>>(`/v1/runs${query(params)}`),

    get: (runId: string): Promise<RunView> =>
      this.http.request<RunView>(`/v1/runs/${encodeURIComponent(runId)}`),

    /**
     * Run を止める。
     *
     * 既定は駆動ループに中断を伝えるだけ（202）。届かない場合に備えて
     * `force` があり、こちらは記録を必ず終端にする（200 / admin 限定）。
     */
    cancel: (runId: string, options: { force?: boolean } = {}): Promise<void> =>
      this.http.request(`/v1/runs/${encodeURIComponent(runId)}/cancel`, {
        method: "POST",
        body: JSON.stringify(options.force === true ? { force: true } : {}),
      }),

    /**
     * コンテナでコマンドを実行する（admin 限定）。
     *
     * 中が見えないと切り分けができないために用意した運用の口。
     */
    exec: (
      runId: string,
      argv: readonly string[],
      options: { timeoutSec?: number } = {},
    ): Promise<{ exitCode: number | null; stdout: string; stderr: string; truncated: boolean }> =>
      this.http.request(`/v1/runs/${encodeURIComponent(runId)}/exec`, {
        method: "POST",
        body: JSON.stringify({ argv: [...argv], ...options }),
      }),

    /** SSE の生の応答。購読は `events.ts` が包む。 */
    /**
     * SSE の生の応答。購読は `events.ts` が包む。
     *
     * `historyOnly` を付けると、実行中の Run でも過去分だけ返して閉じる。
     * 履歴の読み込みは購読ではないので、閉じないと読み切れず固まる。
     */
    eventStream: (
      runId: string,
      options: { lastEventId?: number; signal?: AbortSignal; historyOnly?: boolean } = {},
    ) =>
      this.http.raw(
        `/v1/runs/${encodeURIComponent(runId)}/events${options.historyOnly === true ? "?historyOnly=true" : ""}`,
        {
        headers: {
          accept: "text/event-stream",
          ...(options.lastEventId === undefined
            ? {}
            : { "last-event-id": String(options.lastEventId) }),
        },
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      },
      ),

    artifacts: (runId: string) =>
      this.http.request<{ artifacts: RunView["artifacts"] }>(
        `/v1/runs/${encodeURIComponent(runId)}/artifacts`,
      ),

    /** 成果物の実体。JSON として解釈しない。 */
    artifact: (runId: string, name: string): Promise<Response> =>
      this.http.raw(
        `/v1/runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(name)}`,
      ),

    /**
     * いまコンテナにあるワークスペースの中身。
     *
     * 成果物と違い**保存されていない**ので、コンテナが生きている間だけ取れる。
     */
    workspace: (runId: string) =>
      this.http.request<{ files: readonly { path: string; url: string }[] }>(
        `/v1/runs/${encodeURIComponent(runId)}/workspace`,
      ),

    /** ワークスペースの 1 ファイル。JSON として解釈しない。 */
    workspaceFile: (runId: string, path: string): Promise<Response> =>
      this.http.raw(
        `/v1/runs/${encodeURIComponent(runId)}/workspace/${path
          .split("/")
          .map(encodeURIComponent)
          .join("/")}`,
      ),
  };

  // ------------------------------------------------------------- tenants
  readonly tenants = {
    /** 自分のテナント。横断では返らない。 */
    list: () =>
      this.http.request<{ items: { id: string; name: string }[] }>("/v1/tenants"),
  };

  // ------------------------------------------------------------ projects
  /**
   * テナントの下の入れ物。
   *
   * ★ 一覧・作成・削除だけ。プロジェクトを選ぶと、Environment・バンドル・
   * 資格情報の「居場所」が変わる（それぞれの `list` に `projectId` を渡す）。
   */
  readonly projects = {
    list: (): Promise<Page<ProjectView>> => this.http.request<Page<ProjectView>>("/v1/projects"),

    get: (id: string): Promise<ProjectView> =>
      this.http.request<ProjectView>(`/v1/projects/${encodeURIComponent(id)}`),

    create: (input: { name: string }): Promise<ProjectView> =>
      this.http.request<ProjectView>("/v1/projects", { method: "POST", ...json(input) }),

    /** ★ 属するものをすべて消す。取り消せない。 */
    delete: (id: string): Promise<void> =>
      this.http.request(`/v1/projects/${encodeURIComponent(id)}`, { method: "DELETE" }),
  };

  // ------------------------------------------------------------- uploads
  readonly uploads = {
    /** 単発。小さいファイル用。 */
    single: (
      name: string,
      body: RcaBody,
      options: { contentType?: string; contentLength?: number } = {},
    ): Promise<UploadView> =>
      this.http.request<UploadView>("/v1/uploads", {
        method: "POST",
        body,
        headers: {
          "x-filename": encodeURIComponent(name),
          "content-type": options.contentType ?? "application/octet-stream",
          ...(options.contentLength === undefined
            ? {}
            : { "content-length": String(options.contentLength) }),
        },
      }),

    /** 大きいファイル用の開始。返る宛先へ本体を送る。 */
    begin: (input: {
      name: string;
      contentType?: string;
      size?: number;
    }): Promise<BeginUploadResult> =>
      this.http.request<BeginUploadResult>("/v1/uploads/begin", {
        method: "POST",
        ...json(input),
      }),

    /** 自前で受ける場合の宛先（`upload.direct` が false のとき）。 */
    putContent: (
      uploadId: string,
      body: RcaBody,
      options: { contentLength?: number } = {},
    ): Promise<void> =>
      this.http.request(`/v1/uploads/${encodeURIComponent(uploadId)}/content`, {
        method: "PUT",
        body,
        headers:
          options.contentLength === undefined
            ? {}
            : { "content-length": String(options.contentLength) },
      }),

    complete: (uploadId: string): Promise<UploadView> =>
      this.http.request<UploadView>(`/v1/uploads/${encodeURIComponent(uploadId)}/complete`, {
        method: "POST",
        body: "{}",
      }),
  };

  // ------------------------------------------------------------ sessions
  readonly sessions = {
    create: (input: {
      scope: Scope;
      runnerType: RunnerType;
      idleTimeoutSec?: number;
    }): Promise<SessionView> =>
      this.http.request<SessionView>("/v1/sessions", { method: "POST", ...json(input) }),

    list: (): Promise<Page<SessionView>> => this.http.request<Page<SessionView>>("/v1/sessions"),

    get: (sessionId: string): Promise<SessionView> =>
      this.http.request<SessionView>(`/v1/sessions/${encodeURIComponent(sessionId)}`),

    delete: (sessionId: string): Promise<void> =>
      this.http.request(`/v1/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE" }),
  };

  // -------------------------------------------------------- environments
  readonly environments = {
    /** `projectId` は絞り込みではなく**居場所**。テナントのものも一緒に返る。 */
    list: (place: PlaceQuery = {}): Promise<Page<EnvironmentDoc>> =>
      this.http.request<Page<EnvironmentDoc>>(
        `/v1/environments${placeQuery(place)}`,
      ),

    /** 実効設定のドライラン。 */
    resolve: (
      scope: Scope,
      options: { environmentId?: string } = {},
    ): Promise<ResolvedEnvironment> =>
      this.http.request<ResolvedEnvironment>(
        `/v1/environments/resolve${query({
          tenantId: scope.tenantId,
          projectId: scope.projectId ?? undefined,
          userId: scope.userId ?? undefined,
          environmentId: options.environmentId,
        })}`,
      ),

    get: (id: string): Promise<EnvironmentDoc> =>
      this.http.request<EnvironmentDoc>(`/v1/environments/${encodeURIComponent(id)}`),

    /** 全置換。部分更新は `patch` を使う。 */
    put: (input: {
      id?: string;
      name: string;
      scope: string;
      projectId?: string | null;
      userId?: string | null;
      inherit?: boolean;
      spec: unknown;
    }): Promise<EnvironmentDoc> =>
      this.http.request<EnvironmentDoc>("/v1/environments", { method: "PUT", ...json(input) }),

    /** 部分更新。省略は据え置き、`null` は削除。 */
    patch: (
      id: string,
      input: { name?: string; inherit?: boolean; spec?: Record<string, unknown> },
    ): Promise<EnvironmentDoc> =>
      this.http.request<EnvironmentDoc>(`/v1/environments/${encodeURIComponent(id)}`, {
        method: "PATCH",
        ...json(input),
      }),

    delete: (id: string): Promise<void> =>
      this.http.request(`/v1/environments/${encodeURIComponent(id)}`, { method: "DELETE" }),
  };

  // --------------------------------------------------------- credentials
  readonly credentials = {
    /** `projectId` は絞り込みではなく**居場所**。テナントのものも一緒に返る。 */
    list: (place: PlaceQuery = {}): Promise<Page<CredentialView>> =>
      this.http.request<Page<CredentialView>>(
        `/v1/credentials${placeQuery(place)}`,
      ),

    get: (id: string, place: PlaceQuery = {}): Promise<CredentialView> =>
      this.http.request<CredentialView>(
        `/v1/credentials/${encodeURIComponent(id)}${placeQuery(place)}`,
      ),

    /** 秘密は書き込みのみ。応答には出ない。 */
    create: (input: {
      id?: string;
      kind: string;
      name: string;
      secret: Record<string, string>;
      publicFields?: Record<string, string>;
      /** 作る先の居場所。省略でテナント直下。 */
      projectId?: string | null;
    }): Promise<CredentialView> =>
      this.http.request<CredentialView>("/v1/credentials", { method: "POST", ...json(input) }),

    delete: (id: string, place: PlaceQuery = {}): Promise<void> =>
      this.http.request(
        `/v1/credentials/${encodeURIComponent(id)}${placeQuery(place)}`,
        { method: "DELETE" },
      ),
  };

  // -------------------------------------------------------- asset bundles
  readonly bundles = {
    /** `projectId` は絞り込みではなく**居場所**。テナントのものも一緒に返る。 */
    list: (place: PlaceQuery = {}): Promise<Page<BundleView>> =>
      this.http.request<Page<BundleView>>(
        `/v1/asset-bundles${placeQuery(place)}`,
      ),

    create: (input: { id: string; name: string; projectId?: string | null }): Promise<BundleView> =>
      this.http.request<BundleView>("/v1/asset-bundles", { method: "POST", ...json(input) }),

    delete: (id: string, place: PlaceQuery = {}): Promise<void> =>
      this.http.request(`/v1/asset-bundles/${encodeURIComponent(id)}${placeQuery(place)}`, {
        method: "DELETE",
      }),

    /**
     * 版の中身。`latest` を指定できる。MCP の env は値が伏せられて返る。
     *
     * ★ 版を触る操作にも**居場所**が要る。バンドル自体が居場所から
     * 見えるかを先に確かめるので、渡さないとプロジェクトのバンドルは 404 になる。
     */
    getVersion: (id: string, version = "latest", place: PlaceQuery = {}) =>
      this.http.request<BundleContentView>(
        `/v1/asset-bundles/${encodeURIComponent(id)}/versions/${encodeURIComponent(
          version,
        )}${placeQuery(place)}`,
      ),

    listVersions: (id: string, place: PlaceQuery = {}): Promise<Page<BundleVersionView>> =>
      this.http.request<Page<BundleVersionView>>(
        `/v1/asset-bundles/${encodeURIComponent(id)}/versions${placeQuery(place)}`,
      ),

    /** 中身を丸ごと入れる。tar.gz を直接送ることもできる。 */
    putVersion: (
      id: string,
      content: BundleContent | Uint8Array | ReadableStream<Uint8Array>,
      place: PlaceQuery = {},
    ): Promise<BundleVersionResult> =>
      this.http.request<BundleVersionResult>(
        `/v1/asset-bundles/${encodeURIComponent(id)}/versions${placeQuery(place)}`,
        content instanceof Uint8Array || content instanceof ReadableStream
          ? {
              method: "POST",
              body: content,
              headers: { "content-type": "application/gzip" },
            }
          : { method: "POST", ...json(content) },
      ),

    /** 差分で更新する。省略は据え置き、`null` は削除。 */
    patchVersion: (
      id: string,
      patch: BundlePatch,
      place: PlaceQuery = {},
    ): Promise<BundleVersionResult> =>
      this.http.request<BundleVersionResult>(
        `/v1/asset-bundles/${encodeURIComponent(id)}/versions${placeQuery(place)}`,
        { method: "PATCH", ...json(patch) },
      ),
  };

  // --------------------------------------------------------------- admin
  readonly admin = {
    listApiKeys: (): Promise<Page<ApiKeyView>> =>
      this.http.request<Page<ApiKeyView>>("/v1/api-keys"),

    /** 平文は**この応答にだけ**現れる。 */
    createApiKey: (input: {
      label: string;
      role?: Role;
      projectId?: string | null;
      userId?: string | null;
    }): Promise<IssuedApiKey> =>
      this.http.request<IssuedApiKey>("/v1/api-keys", { method: "POST", ...json(input) }),

    revokeApiKey: (id: string): Promise<void> =>
      this.http.request(`/v1/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),

    logs: (
      params: {
        level?: string;
        minLevel?: string;
        runId?: string;
        text?: string;
        from?: string;
        to?: string;
        limit?: number;
        cursor?: string;
      } = {},
    ): Promise<Page<LogEntry>> => this.http.request<Page<LogEntry>>(`/v1/logs${query(params)}`),

    audit: (params: { limit?: number; cursor?: string } = {}): Promise<Page<AuditEntry>> =>
      this.http.request<Page<AuditEntry>>(`/v1/audit${query(params)}`),

    metrics: (): Promise<Metrics> => this.http.request<Metrics>("/v1/metrics"),

    usage: (
      params: { projectId?: string; userId?: string; from?: string; to?: string } = {},
    ): Promise<UsageAggregate> =>
      this.http.request<UsageAggregate>(`/v1/usage${query(params)}`),
  };
}
